import { PopupButtonType, PopupIconType, showPopup } from "@kalyzee/kast-app-web-components";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageContainer from "../../components/page/PageContainer";
import { CustomCheckBox } from "../../components/utils/CustomCheckBox";
import MultiSelect from "../../components/utils/MultiSelect";
import Select from "../../components/utils/Select";
import { OvenMediaSocketResultEmittedCommand } from "../../helpers/socket";
import { toastError, toastSuccess } from "../../helpers/toast";
import { useSocketAppDispatch } from "../../hooks/app";
import { OmeApplication, OmeApplicationMode } from "../../interfaces/omeApplication";
import {
  StreamProfile,
  StreamProfileLintResult,
  StreamProfileRecord,
  StreamProfileRedirection,
  StreamProfileRevisionEntry,
  StreamProfileRole,
  StreamProfileUpsertResponse,
} from "../../interfaces/streamProfile";
import { useOmeApplications } from "../../store/omeApplication/hooks";
import { useOvenMediaUploadProfiles } from "../../store/context/hooks";
import { useStreamProfileIncidents, useStreamProfiles, useStreamProfilesLoaded } from "../../store/streamProfile/hooks";
import {
  socketDeleteStreamProfile,
  socketGetStreamProfileRevisions,
  socketLintStreamProfile,
  socketUpsertStreamProfile,
} from "../../store/socket/actions";
import styles from "./stream-profiles.module.css";

// "Stream profile" everywhere — never bare "profile" (collides with VOD upload
// profiles). A profile is LIVE on the next admission as soon as it is saved
// (no apply step) — which is also why lint errors block the save server-side.

interface DraftRedirection extends StreamProfileRedirection {}

interface Draft {
  name: string;
  isNew: boolean;
  description: string;
  rolePush: boolean;
  rolePull: boolean;
  sourceApp: string;
  redirections: DraftRedirection[];
  recordEnabled: boolean;
  recordUploadProfileId: string;
  recordExpireAfter: string; // kept as string for the input; parsed on payload
  recordTracks: string[];
  pullKind: "mix" | "ovt";
  multiplexSuffix: string;
  playlistFile: string;
  pathSelector: string;
}

const emptyDraft = (): Draft => ({
  name: "",
  isNew: true,
  description: "",
  rolePush: true,
  rolePull: false,
  sourceApp: "",
  redirections: [],
  recordEnabled: false,
  recordUploadProfileId: "",
  recordExpireAfter: "",
  recordTracks: [],
  pullKind: "mix",
  multiplexSuffix: "_mix",
  playlistFile: "abr",
  pathSelector: "bypass",
});

const draftFromProfile = (profile: StreamProfile): Draft => {
  // Tolerate the deprecated recordTracks (tracks-only, no enable).
  const record = profile.push?.record;
  return {
    name: profile.name,
    isNew: false,
    description: profile.description ?? "",
    rolePush: profile.roles?.includes(StreamProfileRole.PUSH) ?? false,
    rolePull: profile.roles?.includes(StreamProfileRole.PULL) ?? false,
    sourceApp: profile.push?.sourceApp ?? "",
    redirections: JSON.parse(JSON.stringify(profile.push?.redirections ?? [])),
    recordEnabled: record?.enabled ?? false,
    recordUploadProfileId: record?.uploadProfileId ?? "",
    recordExpireAfter: record?.expireAfter !== undefined ? String(record.expireAfter) : "",
    recordTracks: [...(record?.tracks ?? profile.push?.recordTracks ?? [])],
    // Pull is a discriminated union; absent kind reads as "mix".
    pullKind: (profile.pull as any)?.kind === "ovt" ? "ovt" : "mix",
    multiplexSuffix: (profile.pull as any)?.multiplexSuffix ?? "_mix",
    playlistFile: (profile.pull as any)?.playlistFile ?? "abr",
    pathSelector: (profile.pull as any)?.pathSelector ?? "bypass",
  };
};

const draftToPayload = (draft: Draft) => {
  const record: StreamProfileRecord = {};
  if (draft.recordEnabled) record.enabled = true;
  if (draft.recordUploadProfileId) record.uploadProfileId = draft.recordUploadProfileId;
  const expireAfter = Number(draft.recordExpireAfter);
  if (draft.recordExpireAfter && !isNaN(expireAfter) && expireAfter > 0) record.expireAfter = expireAfter;
  if (draft.recordTracks.length) record.tracks = draft.recordTracks;
  const hasRecord = Object.keys(record).length > 0;
  return {
    name: draft.name.trim(),
    description: draft.description || undefined,
    roles: [
      ...(draft.rolePush ? [StreamProfileRole.PUSH] : []),
      ...(draft.rolePull ? [StreamProfileRole.PULL] : []),
    ],
    push: draft.rolePush
      ? {
          sourceApp: draft.sourceApp,
          redirections: draft.redirections,
          ...(hasRecord ? { record } : {}),
        }
      : undefined,
    pull: draft.rolePull
      ? draft.pullKind === "ovt"
        ? { kind: "ovt" as const, pathSelector: draft.pathSelector }
        : { kind: "mix" as const, multiplexSuffix: draft.multiplexSuffix, playlistFile: draft.playlistFile }
      : undefined,
  };
};

// Encode names (auto-names included) of an app's managed config — mirrors the
// middleware's collectAppEncodeNames; feeds the track pickers. The engine's
// auto-name counter increments only for UNNAMED encodes.
const encodeNamesOf = (app?: OmeApplication): string[] => {
  if (!app || app.mode !== OmeApplicationMode.MANAGED || !app.config) return [];
  const names: string[] = [];
  (app.config.outputProfiles?.outputProfile ?? []).forEach((profile: any) => {
    (["videos", "audios"] as const).forEach((kind) => {
      const autoPrefix = kind === "videos" ? "video" : "audio";
      let autoIndex = 0;
      (profile.encodes?.[kind] ?? []).forEach((encode: any) => {
        const named = encode?.name !== undefined && encode?.name !== "";
        const name = named ? String(encode.name) : `${autoPrefix}_${autoIndex++}`;
        if (!names.includes(name)) names.push(name);
      });
    });
  });
  return names;
};

const StreamProfilesPage = () => {
  const socketDispatch = useSocketAppDispatch();
  const profiles = useStreamProfiles();
  const incidents = useStreamProfileIncidents();
  const loaded = useStreamProfilesLoaded();
  const applications = useOmeApplications();
  const uploadProfiles = useOvenMediaUploadProfiles();
  const { profile: profileParam } = useParams();
  const navigate = useNavigate();

  const [draft, setDraft] = useState<Draft>();
  const [lintResults, setLintResults] = useState<StreamProfileLintResult[]>();
  const [busy, setBusy] = useState(false);

  const managedAppNames = applications
    .filter((app) => app.mode === OmeApplicationMode.MANAGED)
    .map((app) => app.name);
  const sourceApp = applications.find((app) => app.name === draft?.sourceApp);
  const sourceEncodeNames = encodeNamesOf(sourceApp);
  const sourceIsUnmanaged = !!draft?.sourceApp && sourceEncodeNames.length === 0;

  const openEditor = (profile?: StreamProfile) => {
    setDraft(profile ? draftFromProfile(profile) : emptyDraft());
    setLintResults(undefined);
  };

  const closeEditor = () => {
    setDraft(undefined);
    setLintResults(undefined);
  };

  // The route param drives the view: /stream-profiles/<name> shows only that
  // profile with its editor open; no param is the list (where new/imported
  // drafts are composed). Not re-run on store refreshes so an open draft isn't
  // clobbered by ON_STREAM_PROFILES; the list and detail routes render this
  // same component, so clearing the param must close the editor here. A param
  // without a store match keeps the draft (a just-saved profile can reach the
  // detail route before the broadcast lands).
  useEffect(() => {
    if (!loaded) return;
    if (!profileParam) {
      closeEditor();
      return;
    }
    const profile = profiles.find((current) => current.name === profileParam);
    if (profile) openEditor(profile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileParam, loaded]);

  const setDraftField = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  };

  const setRedirection = (index: number, patch: Partial<DraftRedirection>) => {
    setDraft((current) => {
      if (!current) return current;
      const redirections = current.redirections.map((entry, i) => (i === index ? { ...entry, ...patch } : entry));
      return { ...current, redirections };
    });
  };

  const runLint = async () => {
    if (!draft) return;
    const result = await socketDispatch<OvenMediaSocketResultEmittedCommand>(socketLintStreamProfile(draftToPayload(draft)));
    if (result?.error) toastError(result.error.message);
    else setLintResults(result?.response?.lintResults ?? []);
  };

  const save = async () => {
    if (!draft) return;
    if (!draft.name.trim()) {
      toastError("Name is required" /* TRANSLATION */);
      return;
    }
    setBusy(true);
    const result = await socketDispatch<OvenMediaSocketResultEmittedCommand<any, StreamProfileUpsertResponse>>(
      socketUpsertStreamProfile(draftToPayload(draft))
    );
    setBusy(false);
    if (result?.error) {
      toastError(`${(result.error as any)?.message ?? ""}`);
      return;
    }
    const response = result?.response;
    setLintResults(response?.lintResults ?? []);
    if (!response?.saved) {
      toastError("Not saved — fix the lint errors below" /* TRANSLATION */);
      return;
    }
    toastSuccess("Saved — live on the next admission" /* TRANSLATION */);
    // A new profile continues on its detail route (the draft carries over until
    // the ON_STREAM_PROFILES broadcast fills the store); an existing one stays
    // open where it is.
    if (draft.isNew) {
      setDraft({ ...draft, isNew: false });
      navigate(`/stream-profiles/${draft.name.trim()}`);
    }
  };

  const confirmDelete = (profile: StreamProfile) => {
    showPopup({
      title: `${"Delete stream profile" /* TRANSLATION */} — ${profile.name}`,
      iconTitle: PopupIconType.INFO,
      enableBackdropDismiss: false,
      enableCloseButton: false,
      content: (
        <div className={styles.popupContent}>
          <div className={styles.popupWarning}>
            {"Publishes using " /* TRANSLATION */}
            <code>{`?profile=${profile.name}`}</code>
            {" will be REJECTED once deleted (an unknown stream profile fails the admission loudly). The last revision keeps a snapshot." /* TRANSLATION */}
          </div>
        </div>
      ),
      buttons: [
        { type: PopupButtonType.CANCEL, element: "Cancel" /* TRANSLATION */ },
        {
          type: PopupButtonType.VALIDATE,
          element: "Delete" /* TRANSLATION */,
          onClick: () => {
            (async () => {
              setBusy(true);
              const result = await socketDispatch<OvenMediaSocketResultEmittedCommand>(
                socketDeleteStreamProfile({ name: profile.name })
              );
              setBusy(false);
              if (result?.error) toastError(`Delete failed: ${(result.error as any)?.message ?? ""}` /* TRANSLATION */);
              else toastSuccess("Deleted" /* TRANSLATION */);
              if (profileParam === profile.name) navigate("/stream-profiles");
              else if (draft?.name === profile.name) closeEditor();
            })();
            return true;
          },
        },
      ],
    });
  };

  const showRevisions = async (profile: StreamProfile) => {
    const result = await socketDispatch<OvenMediaSocketResultEmittedCommand>(
      socketGetStreamProfileRevisions({ name: profile.name, limit: 30 })
    );
    if (result?.error) {
      toastError(result.error.message);
      return;
    }
    const revisions: StreamProfileRevisionEntry[] = result?.response?.revisions ?? [];
    showPopup({
      title: `${"Revision history" /* TRANSLATION */} — ${profile.name}`,
      iconTitle: PopupIconType.INFO,
      content: (
        <div className={styles.revisionList}>
          {revisions.map((revision) => (
            <div key={revision._id} className={styles.revisionRow}>
              <span className={styles.revisionAction}>{revision.action}</span>
              <span>rev {revision.revision}</span>
              <span>{revision.createdAt ? new Date(revision.createdAt).toLocaleString() : ""}</span>
              <span>{revision.author ?? ""}</span>
            </div>
          ))}
        </div>
      ),
      buttons: [{ type: PopupButtonType.OK, element: "OK" }],
    });
  };

  // Duplicate-from-JSON (decision #19): seeding host N+1 is copy-paste.
  const copyJson = (profile: StreamProfile) => {
    const payload = {
      name: profile.name,
      description: profile.description,
      roles: profile.roles,
      push: profile.push,
      pull: profile.pull,
    };
    navigator.clipboard
      .writeText(JSON.stringify(payload, null, 2))
      .then(() => toastSuccess("Stream profile JSON copied" /* TRANSLATION */))
      .catch(() => toastError("Copy failed" /* TRANSLATION */));
  };

  const importJson = () => {
    let textarea: HTMLTextAreaElement | null = null;
    showPopup({
      title: "Import stream profile JSON" /* TRANSLATION */,
      iconTitle: PopupIconType.INFO,
      content: (
        <div className={styles.popupContent}>
          <textarea
            ref={(element) => {
              textarea = element;
            }}
            className={styles.jsonTextarea}
            placeholder='{"name":"abr","roles":["push"],"push":{...}}'
          />
        </div>
      ),
      buttons: [
        { type: PopupButtonType.CANCEL, element: "Cancel" /* TRANSLATION */ },
        {
          type: PopupButtonType.VALIDATE,
          element: "Load in editor" /* TRANSLATION */,
          onClick: () => {
            try {
              const parsed = JSON.parse(textarea?.value ?? "");
              openEditor({ revision: 0, _id: "", _server: "", ...parsed });
              setDraft((current) => (current ? { ...current, isNew: true } : current));
              return true;
            } catch (err) {
              toastError("Invalid JSON" /* TRANSLATION */);
              return false;
            }
          },
        },
      ],
    });
  };

  const trackPicker = (values: string[], onValues: (values: string[]) => void) => {
    if (sourceIsUnmanaged || !draft?.sourceApp) {
      // Free-text fallback (decision #22): raw/unknown source app — the picker
      // cannot be fed, but publishing must stay possible.
      return (
        <input
          className={styles.input}
          value={values.join(";")}
          placeholder={"track names, ; separated" /* TRANSLATION */}
          onChange={(event) =>
            onValues(
              event.currentTarget.value
                .split(";")
                .map((track) => track.trim())
                .filter((track) => track.length)
            )
          }
        />
      );
    }
    return (
      <MultiSelect
        values={values}
        options={sourceEncodeNames.map((name) => ({ value: name, label: name }))}
        onValues={onValues}
      />
    );
  };

  const renderRedirection = (redirection: DraftRedirection, index: number) => (
    <div key={index} className={styles.redirectionCard}>
      <div className={styles.redirectionHeader}>
        <span className={styles.redirectionTitle}>
          {`Rung #${index}${redirection.height ? ` — ${redirection.height}p` : ""}`}
        </span>
        <span>
          <button
            type="button"
            className={styles.iconButton}
            title={"Duplicate" /* TRANSLATION */}
            onClick={() =>
              setDraftField("redirections", [...draft!.redirections, JSON.parse(JSON.stringify(redirection))])
            }
          >
            ⧉
          </button>
          <button
            type="button"
            className={styles.iconButtonDanger}
            title={"Remove" /* TRANSLATION */}
            onClick={() =>
              setDraftField(
                "redirections",
                draft!.redirections.filter((_, i) => i !== index)
              )
            }
          >
            ×
          </button>
        </span>
      </div>
      <div className={styles.fieldGrid}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{"Target app (cloud)" /* TRANSLATION */}</span>
          <input
            className={styles.input}
            value={redirection.targetApp}
            list="stream-profile-app-names"
            onChange={(event) => setRedirection(index, { targetApp: event.currentTarget.value })}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{"Stream suffix" /* TRANSLATION */}</span>
          <input
            className={styles.input}
            value={redirection.streamSuffix}
            placeholder="_{height}"
            onChange={(event) => setRedirection(index, { streamSuffix: event.currentTarget.value })}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{"Height" /* TRANSLATION */}</span>
          <input
            className={styles.input}
            type="number"
            value={redirection.height ?? ""}
            onChange={(event) =>
              setRedirection(index, { height: event.currentTarget.value === "" ? undefined : Number(event.currentTarget.value) })
            }
          />
        </label>
      </div>
      <div className={styles.field}>
        <span className={styles.fieldLabel}>{"Tracks" /* TRANSLATION */}</span>
        {trackPicker(redirection.tracks ?? [], (tracks) => setRedirection(index, { tracks }))}
      </div>
      <details className={styles.advanced}>
        <summary className={styles.advancedSummary}>{"Advanced" /* TRANSLATION */}</summary>
        <div className={styles.fieldGrid}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{"Target URL override" /* TRANSLATION */}</span>
            <input
              className={styles.templateInput}
              value={redirection.targetUrlOverride ?? ""}
              placeholder="{base}?streamid={base}/<app>/{streamKey}_{height}"
              onChange={(event) =>
                setRedirection(index, { targetUrlOverride: event.currentTarget.value || undefined })
              }
            />
          </label>
          <label className={styles.fieldInline}>
            <CustomCheckBox
              isSelected={redirection.disableIfHost !== false}
              setSelected={(on) => setRedirection(index, { disableIfHost: on ? undefined : false })}
            />
            <span className={styles.fieldLabel}>
              {"Skip when target = this host (loop guard)" /* TRANSLATION */}
            </span>
          </label>
        </div>
      </details>
    </div>
  );

  const renderEditor = () => {
    if (!draft) return null;
    return (
      <div className={styles.editor}>
        <datalist id="stream-profile-app-names">
          {managedAppNames.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
        <div className={styles.fieldGrid}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{"Name" /* TRANSLATION */}</span>
            <input
              className={styles.input}
              value={draft.name}
              disabled={!draft.isNew}
              placeholder="abr"
              onChange={(event) => setDraftField("name", event.currentTarget.value)}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{"Description" /* TRANSLATION */}</span>
            <input
              className={styles.input}
              value={draft.description}
              onChange={(event) => setDraftField("description", event.currentTarget.value)}
            />
          </label>
        </div>
        <div className={styles.rolesRow}>
          <label className={styles.fieldInline}>
            <CustomCheckBox isSelected={draft.rolePush} setSelected={(on) => setDraftField("rolePush", on)} />
            <span>{"Push (room: fan-out to the cloud ladder)" /* TRANSLATION */}</span>
          </label>
          <label className={styles.fieldInline}>
            <CustomCheckBox isSelected={draft.rolePull} setSelected={(on) => setDraftField("rolePull", on)} />
            <span>{"Pull (playback: multiplex ABR, or generic edge relay)" /* TRANSLATION */}</span>
          </label>
        </div>

        {draft.rolePush ? (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{"Push" /* TRANSLATION */}</div>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>
                {"Source app (local app the producer pushes into — tracks resolve against it)" /* TRANSLATION */}
              </span>
              <Select
                value={draft.sourceApp}
                options={managedAppNames.map((name) => ({ value: name, label: name }))}
                placeholder={"— select —" /* TRANSLATION */}
                onValue={(value) => setDraftField("sourceApp", value)}
              />
            </label>
            {sourceIsUnmanaged ? (
              <div className={styles.warnBanner}>
                {"This source app has no managed config on this host — track pickers fall back to free text and save-time track checks are skipped." /* TRANSLATION */}
              </div>
            ) : null}
            <div className={styles.subTitleRow}>
              <span className={styles.subTitle}>{`Redirections [${draft.redirections.length}]`}</span>
              <button
                type="button"
                className={styles.addButton}
                onClick={() =>
                  setDraftField("redirections", [
                    ...draft.redirections,
                    { targetApp: "", streamSuffix: "_{height}", tracks: [] },
                  ])
                }
              >
                {"+ rung" /* TRANSLATION */}
              </button>
            </div>
            {draft.redirections.map(renderRedirection)}
            <div className={styles.subTitleRow}>
              <span className={styles.subTitle}>{"Recording" /* TRANSLATION */}</span>
            </div>
            <label className={styles.fieldInline}>
              <CustomCheckBox
                isSelected={draft.recordEnabled}
                setSelected={(on) => setDraftField("recordEnabled", on)}
              />
              <span>{"Record by default (a bare URL records; override on the URL with record=false)" /* TRANSLATION */}</span>
            </label>
            {draft.recordEnabled ? (
              <>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{"Upload profile (VOD destination)" /* TRANSLATION */}</span>
                  <Select
                    value={draft.recordUploadProfileId}
                    options={uploadProfiles.map((profile) => ({ value: profile.id, label: profile.name ?? profile.id }))}
                    placeholder={"— select —" /* TRANSLATION */}
                    onValue={(value) => setDraftField("recordUploadProfileId", value)}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{"Expire after (ms, optional)" /* TRANSLATION */}</span>
                  <input
                    className={styles.input}
                    type="number"
                    value={draft.recordExpireAfter}
                    onChange={(event) => setDraftField("recordExpireAfter", event.currentTarget.value)}
                  />
                </label>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{"Record tracks (empty = engine default)" /* TRANSLATION */}</span>
                  {trackPicker(draft.recordTracks, (tracks) => setDraftField("recordTracks", tracks))}
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        {draft.rolePull ? (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{"Pull" /* TRANSLATION */}</div>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{"Topology" /* TRANSLATION */}</span>
              <Select
                value={draft.pullKind}
                options={[
                  { value: "mix", label: "mix — ABR multiplex playback" /* TRANSLATION */ },
                  { value: "ovt", label: "ovt — generic edge relay" /* TRANSLATION */ },
                ]}
                onValue={(value) => setDraftField("pullKind", value === "ovt" ? "ovt" : "mix")}
              />
            </label>
            {draft.pullKind === "ovt" ? (
              <label className={styles.field}>
                <span className={styles.fieldLabel}>
                  {"Path selector (playlist/rendition segment, e.g. \"bypass\")" /* TRANSLATION */}
                </span>
                <input
                  className={styles.input}
                  value={draft.pathSelector}
                  onChange={(event) => setDraftField("pathSelector", event.currentTarget.value)}
                />
              </label>
            ) : (
              <div className={styles.fieldGrid}>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{"Multiplex suffix" /* TRANSLATION */}</span>
                  <input
                    className={styles.input}
                    value={draft.multiplexSuffix}
                    onChange={(event) => setDraftField("multiplexSuffix", event.currentTarget.value)}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{"Playlist file" /* TRANSLATION */}</span>
                  <input
                    className={styles.input}
                    value={draft.playlistFile}
                    onChange={(event) => setDraftField("playlistFile", event.currentTarget.value)}
                  />
                </label>
              </div>
            )}
          </div>
        ) : null}

        {lintResults?.length ? (
          <div className={styles.lintPanel}>
            {lintResults.map((result, index) => (
              <div key={index} className={result.severity === "error" ? styles.lintError : styles.lintWarn}>
                [{result.severity}] {result.id} — {result.message}
              </div>
            ))}
          </div>
        ) : null}

        <div className={styles.editorButtons}>
          <button type="button" className={styles.actionButton} disabled={busy} onClick={runLint}>
            {"Lint" /* TRANSLATION */}
          </button>
          <button type="button" className={styles.actionButton} disabled={busy} onClick={save}>
            {"Save" /* TRANSLATION */}
          </button>
          <button
            type="button"
            className={styles.actionButton}
            disabled={busy}
            onClick={() => (draft.isNew ? closeEditor() : navigate("/stream-profiles"))}
          >
            {"Close" /* TRANSLATION */}
          </button>
        </div>
      </div>
    );
  };

  const renderProfileHeader = (profile: StreamProfile, showEdit: boolean) => (
    <div className={styles.profileRow}>
      <span className={styles.profileName}>{profile.name}</span>
      <span className={styles.badges}>
        {profile.roles?.map((role) => (
          <span key={role} className={styles.badgeRole}>
            {role}
          </span>
        ))}
        <span className={styles.revision}>{`rev ${profile.revision}`}</span>
      </span>
      <span className={styles.profileActions}>
        {showEdit ? (
          <button type="button" className={styles.actionButton} disabled={busy} onClick={() => navigate(`/stream-profiles/${profile.name}`)}>
            {"Edit" /* TRANSLATION */}
          </button>
        ) : null}
        <button type="button" className={styles.actionButton} disabled={busy} onClick={() => copyJson(profile)}>
          {"Copy JSON" /* TRANSLATION */}
        </button>
        <button type="button" className={styles.actionButton} disabled={busy} onClick={() => showRevisions(profile)}>
          {"History" /* TRANSLATION */}
        </button>
        <button type="button" className={styles.actionButtonDanger} disabled={busy} onClick={() => confirmDelete(profile)}>
          {"Delete" /* TRANSLATION */}
        </button>
      </span>
    </div>
  );

  const renderBackLink = () => (
    <button type="button" className={styles.backLink} onClick={() => navigate("/stream-profiles")}>
      {"← All stream profiles" /* TRANSLATION */}
    </button>
  );

  // List route: composing a new/imported profile takes over the page (only the
  // editor shows); otherwise incidents + toolbar + one row per profile. Detail
  // route: only the routed profile, editor open.
  const renderList = () => {
    if (draft?.isNew) return renderEditor();
    return (
      <>
        {incidents.length ? (
          <div className={styles.incidentBanner}>
            <b>{"Degraded track resolution" /* TRANSLATION */}</b>
            {incidents.slice(0, 5).map((incident, index) => (
              <div key={index} className={styles.incidentRow}>
                {`${new Date(incident.at).toLocaleString()} — ${incident.kind} on ${incident.app}/${incident.stream}: missing [${incident.missing.join(", ")}]`}
              </div>
            ))}
          </div>
        ) : null}

        <div className={styles.toolbar}>
          <button type="button" className={styles.actionButton} disabled={busy} onClick={() => openEditor()}>
            {"+ New stream profile" /* TRANSLATION */}
          </button>
          <button type="button" className={styles.actionButton} disabled={busy} onClick={importJson}>
            {"Import JSON" /* TRANSLATION */}
          </button>
        </div>

        {profiles.map((profile) => (
          <div key={profile.name} className={styles.profileBlock}>
            {renderProfileHeader(profile, true)}
            {profile.description ? <div className={styles.description}>{profile.description}</div> : null}
          </div>
        ))}
      </>
    );
  };

  const renderDetail = () => {
    const profile = profiles.find((current) => current.name === profileParam);
    // A just-saved profile can reach this route before the store broadcast
    // lands — the surviving draft stands in until then.
    const draftMatches = !!draft && !draft.isNew && draft.name === profileParam;
    if (!profile && !draftMatches) {
      return (
        <>
          {renderBackLink()}
          <div className={styles.notFound}>
            {`Stream profile "${profileParam}" was not found — it may have been deleted or renamed.` /* TRANSLATION */}
          </div>
        </>
      );
    }
    return (
      <>
        {renderBackLink()}
        <div className={styles.profileBlock}>
          {profile ? renderProfileHeader(profile, false) : null}
          {profile?.description ? <div className={styles.description}>{profile.description}</div> : null}
          {draftMatches ? renderEditor() : null}
        </div>
      </>
    );
  };

  return (
    <PageContainer
      title={profileParam ? `${"Stream profiles" /* TRANSLATION */} — ${profileParam}` : "Stream profiles" /* TRANSLATION */}
      loading={!loaded}
    >
      <div className={styles.container}>{profileParam ? renderDetail() : renderList()}</div>
    </PageContainer>
  );
};

export default StreamProfilesPage;
